import { Switch } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormLabel from '@material-ui/core/FormLabel';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import Stepper from '@material-ui/core/Stepper';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { InfoOutlined } from '@material-ui/icons';
import LockIcon from '@material-ui/icons/Lock';
import PublicIcon from '@material-ui/icons/Public';
import { useState } from 'react';
import Tooltip from '../../../../common/Tooltip';
import { toastError, toastSuccess } from '../../../../utils';
import UserList from '../../UserList';
import { updateEstimateContract } from '../actions';

const ShareToOperationModel = ({ selectedContractId, currentUserId, handleClose, refetch, isOtherTab = false }) => {
    const [activeStep, setActiveStep] = useState(0);
    const [shareType, setShareType] = useState('private');
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [operationName, setOperationName] = useState('');
    const [isTeamView, setIsTeamView] = useState(false);

    const steps = [
        'Select Sharing Type',
        shareType === 'private' ? 'Select Users' : 'Confirm Sharing',
        ...(shareType === 'private' ? ['Confirm Sharing'] : []),
    ];

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleShareTypeChange = (event) => {
        setShareType(event.target.value);
        setActiveStep(0);
    };

    const handleUserSelectionChange = (users) => {
        setSelectedUsers(users);
    };

    const handleOperationNameChange = (event) => {
        setOperationName(event.target.value);
    };

    const handleTeamViewChange = (event) => {
        setIsTeamView(event.target.checked);
    };

    const handleSubmit = async () => {
        try {
            const payload = {
                assignedTo: shareType === 'private' ? selectedUsers[0].id : null,
                assignee: currentUserId,
                status: 'close',
                operationAccess: shareType === 'private' ? 'private' : 'public',
                isCreateCopy: !isOtherTab,
                operationName: operationName,
                isTeamView,
                isOtherTab
            }
            await updateEstimateContract({ _id: selectedContractId }, payload)
            setSelectedUsers([]);
            setOperationName('');
            setActiveStep(0);
            handleClose();
            refetch();
            toastSuccess('Operation shared successfully!');
        } catch (error) {
            toastError('Error sharing operation!');
            console.error('Error sharing operation:', error);
        }
    }

    // No separate ConfirmationMessage component

    const renderStepContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <div className="w-full">
                        <FormControl component="fieldset" className="w-full" fullWidth>
                            <FormLabel component="legend" className="text-gray-700 text-center font-medium text-sm mb-3">
                                How would you like to share this operation?
                            </FormLabel>
                            <RadioGroup
                                aria-label="share-type"
                                name="share-type"
                                value={shareType}
                                onChange={handleShareTypeChange}
                                className="mt-1 !grid grid-cols-1 sm:grid-cols-2 gap-3 w-full"
                            >
                                <div className={`flex items-center p-3 border rounded-none w-full ${shareType === 'private' ? 'border-2 border-[#313C4C] bg-sky-50/60' : 'border-gray-300 hover:bg-gray-50'}`}>
                                    <FormControlLabel
                                        value="private"
                                        control={<Radio color="primary" size="small" />}
                                        label={
                                            <div className="flex items-center">
                                                <LockIcon className={`mr-2 ${shareType === 'private' ? 'text-[#313C4C]' : 'text-gray-500'}`} style={{ fontSize: 18 }} />
                                                <div>
                                                    <Typography variant="subtitle2" className="font-semibold text-xs text-gray-800">Share as Private</Typography>
                                                    <Typography variant="caption" className="text-gray-600 text-[11px]">
                                                        Only selected users will have access
                                                    </Typography>
                                                </div>
                                            </div>
                                        }
                                        className="w-full m-0"
                                    />
                                </div>
                                <div className={`flex items-center p-3 border rounded-none w-full ${shareType === 'common' ? 'border-2 border-[#313C4C] bg-sky-50/60' : 'border-gray-300 hover:bg-gray-50'}`}>
                                    <FormControlLabel
                                        value="common"
                                        control={<Radio color="primary" size="small" />}
                                        label={
                                            <div className="flex items-center">
                                                <PublicIcon className={`mr-2 ${shareType === 'common' ? 'text-[#313C4C]' : 'text-gray-500'}`} style={{ fontSize: 18 }} />
                                                <div>
                                                    <Typography variant="subtitle2" className="font-semibold text-xs text-gray-800">Share as Common</Typography>
                                                    <Typography variant="caption" className="text-gray-600 text-[11px]">
                                                        All users will have access
                                                    </Typography>
                                                </div>
                                            </div>
                                        }
                                        className="w-full m-0"
                                    />
                                </div>
                            </RadioGroup>
                        </FormControl>
                        <div className="absolute bottom-0 right-0 left-0 border-t border-gray-300 bg-white w-full px-4 py-2 flex justify-end gap-2">
                            <Button
                                variant="contained"
                                onClick={handleNext}
                                disabled={!shareType}
                                className="!rounded-none !text-[12px] !font-semibold !px-5 !py-1.5 !bg-[#313C4C] hover:!bg-[#1e2632] !text-white disabled:!bg-gray-300 disabled:!text-gray-500 shadow-none"
                            >
                                NEXT
                            </Button>
                        </div>
                    </div>
                );
            case 1:
                return shareType === 'private' ? (
                    <div className="mt-1 pb-16">
                        <Typography variant="body2" className="mb-2 text-gray-700 font-medium text-xs">
                            Select users to share with:
                        </Typography>
                        <UserList onSelectionChange={handleUserSelectionChange} isSelection selectedUsers={selectedUsers} setSelectedUsers={setSelectedUsers} />
                        <div className="absolute bottom-0 right-0 left-0 border-t border-gray-300 bg-white w-full px-4 py-2 flex justify-end gap-2">
                            <Button
                                onClick={handleBack}
                                variant="outlined"
                                className="!rounded-none !text-[12px] !font-semibold !px-4 !py-1.5 !border-gray-400 !text-gray-700 hover:!bg-gray-100"
                            >
                                BACK
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleNext}
                                disabled={selectedUsers.length === 0}
                                className="!rounded-none !text-[12px] !font-semibold !px-5 !py-1.5 !bg-[#313C4C] hover:!bg-[#1e2632] !text-white disabled:!bg-gray-300 disabled:!text-gray-500 shadow-none"
                            >
                                NEXT
                            </Button>
                        </div>
                    </div>
                ) : (
                    // Direct Confirmation UI for common sharing
                    <div className="mx-auto p-3 pb-16 bg-white rounded-none border border-gray-200">
                        <div className="text-center mb-3">
                            <InfoOutlined color='primary' fontSize='medium' className='mb-1' />
                            <h3 className="text-base font-semibold text-gray-800">
                                Confirm Public Sharing
                            </h3>
                            <p className="text-xs text-gray-600 mt-0.5">
                                You're about to share this publicly with all users
                            </p>
                        </div>
                        <div className="mb-3">
                            <TextField
                                fullWidth
                                size="small"
                                label="Operation Name"
                                variant="outlined"
                                value={operationName}
                                onChange={handleOperationNameChange}
                                required
                                helperText="Please provide a name for this operation"
                            />
                        </div>
                        <div className="bg-rose-50 border-l-4 border-rose-500 p-2.5 rounded-none mb-3">
                            <p className="text-xs text-gray-700">
                                <span className="font-bold text-rose-700">Important:</span> Once shared, this contract will be marked as 'closed'
                                and will no longer be editable.
                            </p>
                        </div>
                        <div className="absolute bottom-0 right-0 left-0 border-t border-gray-300 bg-white w-full px-4 py-2 flex justify-end gap-2">
                            <Button
                                onClick={handleBack}
                                variant="outlined"
                                className="!rounded-none !text-[12px] !font-semibold !px-4 !py-1.5 !border-gray-400 !text-gray-700 hover:!bg-gray-100"
                            >
                                BACK
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleSubmit}
                                disabled={!operationName}
                                className="!rounded-none !text-[12px] !font-semibold !px-5 !py-1.5 !bg-[#313C4C] hover:!bg-[#1e2632] !text-white disabled:!bg-gray-300 disabled:!text-gray-500 shadow-none"
                            >
                                FINISH
                            </Button>
                        </div>
                    </div>
                );
            case 2:
                return (
                    // Direct Confirmation UI for private sharing
                    <div className="mx-auto p-3 pb-16 bg-white rounded-none border border-gray-200">
                        <div className="text-center mb-3">
                            <InfoOutlined color='primary' fontSize='medium' className='mb-1' />
                            <h3 className="text-base font-semibold text-gray-800">
                                Confirm Private Sharing
                            </h3>
                            <p className="text-xs text-gray-600 mt-0.5">
                                You're about to share with {selectedUsers[0]?.name}
                            </p>
                        </div>
                        <div className="mb-3">
                            <TextField
                                fullWidth
                                size="small"
                                label="Operation Name"
                                variant="outlined"
                                value={operationName}
                                onChange={handleOperationNameChange}
                                required
                                helperText="Please provide a name for this operation"
                            />
                        </div>
                        <div className="mb-3 flex items-center gap-2">
                            <Switch
                                size="small"
                                checked={isTeamView}
                                onChange={handleTeamViewChange}
                                name="teamView"
                                color="primary"
                            />
                            <span className="flex items-center gap-1 text-xs text-gray-700">
                                <span>
                                    Team View
                                </span>
                                <Tooltip text="All users in the team will be able to view this operation" position="right">
                                    <InfoOutlined color='primary' style={{ fontSize: 14 }} />
                                </Tooltip>
                            </span>
                        </div>
                        <div className="bg-rose-50 border-l-4 border-rose-500 p-2.5 rounded-none mb-3">
                            <p className="text-xs text-gray-700">
                                <span className="font-bold text-rose-700">Important:</span> Once shared, this contract will be marked as 'closed'
                                and will no longer be editable.
                            </p>
                        </div>
                        <div className="absolute bottom-0 right-0 left-0 border-t border-gray-300 bg-white w-full px-4 py-2 flex justify-end gap-2">
                            <Button
                                onClick={handleBack}
                                variant="outlined"
                                className="!rounded-none !text-[12px] !font-semibold !px-4 !py-1.5 !border-gray-400 !text-gray-700 hover:!bg-gray-100"
                            >
                                BACK
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleSubmit}
                                disabled={!operationName}
                                className="!rounded-none !text-[12px] !font-semibold !px-5 !py-1.5 !bg-[#313C4C] hover:!bg-[#1e2632] !text-white disabled:!bg-gray-300 disabled:!text-gray-500 shadow-none"
                            >
                                FINISH
                            </Button>
                        </div>
                    </div>
                );
            default:
                return 'Unknown step';
        }
    };

    return (
        <div className="w-full relative min-h-[300px] flex flex-col">
            <Stepper
                activeStep={activeStep}
                className="stepper-background"
                style={{ background: "#fff" }}
                alternativeLabel
            >
                {steps.map((label) => (
                    <Step style={{ background: "#fff" }} key={label}>
                        <StepLabel style={{ background: "#fff" }}><span className="hidden sm:inline">{label}</span></StepLabel>
                    </Step>
                ))}
            </Stepper>
            {activeStep < steps.length && (
                <div className="sm:hidden text-center text-[11px] font-medium text-slate-900 -mt-1 mb-2">
                    Step {activeStep + 1} of {steps.length}: {steps[activeStep]}
                </div>
            )}

            {/* Step content */}
            <Box mb={activeStep === 0 ? 0 : 1} className={activeStep === 0 ? "flex flex-1 items-center justify-center pb-14" : ""}>
                {renderStepContent(activeStep)}
            </Box>
        </div>
    );
};

export default ShareToOperationModel;
